import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { 
  Server, 
  Database, 
  Loader2, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle,
  Wrench
} from 'lucide-react';

/**
 * SystemHealthCard - Displays system health status and skill seeding tools
 * Extracted from Settings.js for maintainability
 */
export const SystemHealthCard = ({ api }) => {
  const [systemHealth, setSystemHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [repairingSkills, setRepairingSkills] = useState(false);

  const checkSystemHealth = async () => {
    setHealthLoading(true);
    try {
      const response = await api.get('/job-simulations/admin/system-health');
      setSystemHealth(response.data);
    } catch (error) {
      toast.error('Failed to check system health');
    } finally {
      setHealthLoading(false);
    }
  };

  const seedAllSkills = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/job-simulations/initialize-all');
      toast.success(`Skills seeded! ${response.data.total_skills} skills initialized`);
      await checkSystemHealth();
    } catch (error) {
      toast.error('Failed to seed skills');
    } finally {
      setSeeding(false);
    }
  };

  const repairITSkills = async () => {
    setRepairingSkills(true);
    try {
      const response = await api.post('/job-simulations/admin/repair-it-skills');
      if (response.data.skills_repaired > 0) {
        toast.success(`IT Skills repaired! ${response.data.skills_repaired} skills fixed. Total active: ${response.data.active_it_skills}`);
      } else {
        toast.info(`IT Skills already OK. ${response.data.active_it_skills} active skills found.`);
      }
      await checkSystemHealth();
    } catch (error) {
      toast.error('Failed to repair IT skills');
    } finally {
      setRepairingSkills(false);
    }
  };

  return (
    <Card className="border-2 border-emerald-500/20 bg-gradient-to-r from-emerald-500/5 to-cyan-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="h-5 w-5 text-emerald-500" />
          System Health & Skills
        </CardTitle>
        <CardDescription>
          Monitor system status and seed skills data for Job Simulations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Status Overview */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">System Status</Label>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={checkSystemHealth}
                disabled={healthLoading}
                data-testid="check-health-btn"
              >
                {healthLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            {systemHealth ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {systemHealth.status === 'healthy' ? (
                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Healthy
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Issues Found
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-background border">
                    <p className="text-muted-foreground">Skills</p>
                    <p className="text-2xl font-bold">{systemHealth.database_stats?.skills || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background border">
                    <p className="text-muted-foreground">Playbooks</p>
                    <p className="text-2xl font-bold">{systemHealth.database_stats?.playbooks || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background border">
                    <p className="text-muted-foreground">Verticals</p>
                    <p className="text-2xl font-bold">{systemHealth.database_stats?.verticals || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background border">
                    <p className="text-muted-foreground">Coverage</p>
                    <p className="text-2xl font-bold">{systemHealth.database_stats?.playbook_coverage_percent || 0}%</p>
                  </div>
                </div>
                
                {/* LLM Status */}
                <div className="p-3 rounded-lg bg-background border">
                  <p className="text-sm font-medium mb-2">LLM Configuration</p>
                  <div className="flex items-center gap-2">
                    <Badge variant={systemHealth.llm_config?.lemonfox_configured ? "default" : "outline"}>
                      Lemonfox: {systemHealth.llm_config?.lemonfox_configured ? '✓' : '✗'}
                    </Badge>
                    <Badge variant={systemHealth.llm_config?.emergent_configured ? "default" : "outline"}>
                      Emergent: {systemHealth.llm_config?.emergent_configured ? '✓' : '✗'}
                    </Badge>
                  </div>
                </div>
                
                {/* Issues */}
                {systemHealth.issues?.length > 0 && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-sm font-medium text-amber-600 mb-1">Issues:</p>
                    <ul className="text-xs space-y-1">
                      {systemHealth.issues.map((issue, idx) => (
                        <li key={idx}>• {issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Server className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Click refresh to check system health</p>
              </div>
            )}
          </div>
          
          {/* Seed Skills Action */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Seed Skills Data</Label>
            <p className="text-sm text-muted-foreground">
              Initialize all skill verticals (IT, Engineering, Pharma & Healthcare) with comprehensive training data. 
              Safe to run multiple times - will skip already existing data.
            </p>
            
            <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Database className="h-4 w-4 text-primary" />
                <span className="font-medium">This will seed:</span>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• <strong>IT Vertical</strong>: Software, Cloud, Data, Security skills</li>
                <li>• <strong>Engineering Vertical</strong>: Mechanical, Electrical, Civil skills</li>
                <li>• <strong>Pharma & Healthcare</strong>: Clinical, Research, QA skills</li>
              </ul>
            </div>
            
            <Button 
              onClick={seedAllSkills}
              disabled={seeding}
              className="w-full"
              data-testid="seed-skills-btn"
            >
              {seeding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Seeding Skills...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Seed All Skills
                </>
              )}
            </Button>
            
            {systemHealth?.database_stats?.skills === 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                No skills found. Click above to seed skill data.
              </p>
            )}
            
            {systemHealth?.database_stats?.skills > 0 && (
              <p className="text-xs text-emerald-600 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                {systemHealth.database_stats.skills} skills already seeded
              </p>
            )}
            
            {/* Repair IT Skills Button */}
            {systemHealth?.database_stats?.skills > 0 && (
              <div className="pt-4 border-t">
                <Label className="text-sm font-medium text-amber-600">IT Skills Not Showing?</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  If IT skills show category counts but &quot;No skills found&quot; when clicked, use this repair.
                </p>
                <Button 
                  onClick={repairITSkills}
                  disabled={repairingSkills}
                  variant="outline"
                  className="w-full border-amber-500/50 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                  data-testid="repair-it-skills-btn"
                >
                  {repairingSkills ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Repairing IT Skills...
                    </>
                  ) : (
                    <>
                      <Wrench className="h-4 w-4 mr-2" />
                      Repair IT Skills
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemHealthCard;
